/**
 * Tours por seção de /settings (um botão "?" dentro de cada seção aberta).
 * Mesmo formato de appTours.ts. Ordem dos idiomas em d() e s(): pt, en, es, fr, it, de, hu, ja, zh, ru.
 */
import { TOUR_LANGS, Localized, Pair, StudyTourDef, TourStepDef } from './studyTours';

export type SettingsTourKey =
  | 'settings_preferences' | 'settings_translation' | 'settings_storage'
  | 'settings_sounds' | 'settings_update' | 'settings_upgrade';

const d = (...v: string[]) => Object.fromEntries(TOUR_LANGS.map((l, i) => [l, v[i]])) as Localized<string>;
const s = (target: string, ...pairs: Pair[]): TourStepDef => ({
  target,
  text: Object.fromEntries(TOUR_LANGS.map((l, i) => [l, pairs[i]])) as Localized<Pair>,
});

const SECTION = '[data-tour="settings-section-content"]';

export const SETTINGS_TOURS: Record<SettingsTourKey, StudyTourDef> = {
  settings_preferences: {
    description: d('Identidade e aparência.', 'Identity and appearance.', 'Identidad y apariencia.', 'Identité et apparence.', 'Identità e aspetto.', 'Identität und Aussehen.', 'Azonosító és megjelenés.', 'プロフィールと外観。', '身份与外观。', 'Профиль и оформление.'),
    steps: [
      s('[data-tour="sec-pref-id"]',
        ['Seu perfil', 'Defina seu @id único e uma bio curta que aparece para seus amigos.'],
        ['Your profile', 'Set your unique @id and a short bio your friends will see.'],
        ['Tu perfil', 'Define tu @id único y una breve biografía que verán tus amigos.'],
        ['Votre profil', 'Choisissez votre @id unique et une courte bio visible par vos amis.'],
        ['Il tuo profilo', 'Imposta il tuo @id unico e una breve bio visibile agli amici.'],
        ['Dein Profil', 'Lege deine eindeutige @id und eine kurze Bio für deine Freunde fest.'],
        ['A profilod', 'Állítsd be az egyedi @azonosítód és egy rövid bemutatkozást.'],
        ['プロフィール', '固有の @id と、友達に表示される短い自己紹介を設定します。'],
        ['你的资料', '设置唯一的 @id 和好友可见的简短介绍。'],
        ['Ваш профиль', 'Задайте уникальный @id и короткое описание для друзей.'],
      ),
      s('[data-tour="sec-pref-theme"]',
        ['Cor do tema', 'Escolha a cor de destaque usada em botões e detalhes do app.'],
        ['Theme color', 'Pick the accent color used on buttons and highlights.'],
        ['Color del tema', 'Elige el color de acento de botones y detalles.'],
        ['Couleur du thème', 'Choisissez la couleur d’accent des boutons et détails.'],
        ['Colore del tema', 'Scegli il colore d’accento di pulsanti e dettagli.'],
        ['Themenfarbe', 'Wähle die Akzentfarbe für Buttons und Details.'],
        ['Témaszín', 'Válaszd ki a gombok és kiemelések színét.'],
        ['テーマカラー', 'ボタンやアクセントに使う色を選びます。'],
        ['主题颜色', '选择按钮和细节使用的强调色。'],
        ['Цвет темы', 'Выберите акцентный цвет кнопок и деталей.'],
      ),
      s('[data-tour="sec-pref-display"]',
        ['Exibição no perfil', 'Decida o que outras pessoas veem no seu perfil, como nível e conquistas.'],
        ['Profile display', 'Decide what others see on your profile, like level and achievements.'],
        ['Visibilidad del perfil', 'Decide qué ven otros en tu perfil, como nivel y logros.'],
        ['Affichage du profil', 'Choisissez ce que les autres voient : niveau, succès…'],
        ['Visualizzazione profilo', 'Decidi cosa vedono gli altri: livello e traguardi.'],
        ['Profilanzeige', 'Bestimme, was andere sehen, etwa Level und Erfolge.'],
        ['Profil megjelenítése', 'Döntsd el, mit látnak mások: szint, eredmények.'],
        ['プロフィール表示', 'レベルや実績など、他の人に見せる項目を選びます。'],
        ['资料展示', '决定别人能看到什么，比如等级和成就。'],
        ['Показ в профиле', 'Решите, что видят другие: уровень, достижения.'],
      ),
      s('[data-tour="sec-pref-save"]',
        ['Salvar', 'Toque aqui para gravar as alterações.'],
        ['Save', 'Tap here to save your changes.'],
        ['Guardar', 'Toca aquí para guardar los cambios.'],
        ['Enregistrer', 'Touchez ici pour enregistrer les modifications.'],
        ['Salva', 'Tocca qui per salvare le modifiche.'],
        ['Speichern', 'Tippe hier, um die Änderungen zu speichern.'],
        ['Mentés', 'Koppints ide a módosítások mentéséhez.'],
        ['保存', 'ここをタップして変更を保存します。'],
        ['保存', '点击这里保存更改。'],
        ['Сохранить', 'Нажмите, чтобы сохранить изменения.'],
      ),
    ],
  },

  settings_translation: {
    description: d('Idioma nativo, tradutor offline e vozes.', 'Native language, offline translator and voices.', 'Idioma nativo, traductor sin conexión y voces.', 'Langue maternelle, traducteur hors ligne et voix.', 'Lingua madre, traduttore offline e voci.', 'Muttersprache, Offline-Übersetzer und Stimmen.', 'Anyanyelv, offline fordító és hangok.', '母語、オフライン翻訳、音声。', '母语、离线翻译和语音。', 'Родной язык, офлайн-переводчик и голоса.'),
    steps: [
      s('[data-tour="sec-tr-native"]',
        ['Idioma nativo', 'É o idioma das traduções e dos menus do app.'],
        ['Native language', 'The language for translations and the app menus.'],
        ['Idioma nativo', 'Es el idioma de las traducciones y los menús.'],
        ['Langue maternelle', 'La langue des traductions et des menus.'],
        ['Lingua madre', 'La lingua delle traduzioni e dei menu.'],
        ['Muttersprache', 'Die Sprache der Übersetzungen und Menüs.'],
        ['Anyanyelv', 'A fordítások és a menük nyelve.'],
        ['母語', '翻訳とメニューに使う言語です。'],
        ['母语', '翻译和菜单所用的语言。'],
        ['Родной язык', 'Язык переводов и меню приложения.'],
      ),
      s('[data-tour="sec-tr-offline"]',
        ['Tradutor offline (PRO)', 'Baixe o tradutor de frases e o dicionário de palavras para traduzir sem internet.'],
        ['Offline translator (PRO)', 'Download the sentence translator and word dictionary to translate without internet.'],
        ['Traductor sin conexión (PRO)', 'Descarga el traductor de frases y el diccionario para traducir sin internet.'],
        ['Traducteur hors ligne (PRO)', 'Téléchargez le traducteur de phrases et le dictionnaire pour traduire sans internet.'],
        ['Traduttore offline (PRO)', 'Scarica il traduttore di frasi e il dizionario per tradurre senza internet.'],
        ['Offline-Übersetzer (PRO)', 'Lade Satzübersetzer und Wörterbuch, um ohne Internet zu übersetzen.'],
        ['Offline fordító (PRO)', 'Töltsd le a mondatfordítót és a szótárt internet nélküli fordításhoz.'],
        ['オフライン翻訳 (PRO)', '文の翻訳エンジンと単語辞書をダウンロードすると、ネットなしで翻訳できます。'],
        ['离线翻译 (PRO)', '下载句子翻译器和单词词典，无网络也能翻译。'],
        ['Офлайн-переводчик (PRO)', 'Скачайте переводчик фраз и словарь, чтобы переводить без интернета.'],
      ),
      s('[data-tour="sec-tr-audio"]',
        ['Áudio pré-gravado', 'Usa gravações de estúdio para a pronúncia de palavras isoladas.'],
        ['Pre-recorded audio', 'Uses studio recordings for single-word pronunciation.'],
        ['Audio pregrabado', 'Usa grabaciones de estudio para palabras sueltas.'],
        ['Audio préenregistré', 'Utilise des enregistrements studio pour les mots isolés.'],
        ['Audio preregistrato', 'Usa registrazioni in studio per le singole parole.'],
        ['Vorab-Aufnahmen', 'Nutzt Studioaufnahmen für einzelne Wörter.'],
        ['Előre rögzített hang', 'Stúdiófelvételeket használ az egyes szavakhoz.'],
        ['録音音声', '単語の発音にスタジオ録音を使います。'],
        ['预录音频', '单词发音使用录音棚录音。'],
        ['Записанное аудио', 'Студийные записи для отдельных слов.'],
      ),
      s('[data-tour="sec-tr-voices"]',
        ['Vozes offline (PRO)', 'Baixe vozes neurais para ouvir a pronúncia sem conexão.'],
        ['Offline voices (PRO)', 'Download neural voices to hear pronunciation offline.'],
        ['Voces sin conexión (PRO)', 'Descarga voces neuronales para escuchar sin conexión.'],
        ['Voix hors ligne (PRO)', 'Téléchargez des voix neuronales pour écouter hors ligne.'],
        ['Voci offline (PRO)', 'Scarica voci neurali per ascoltare offline.'],
        ['Offline-Stimmen (PRO)', 'Lade neuronale Stimmen, um offline zu hören.'],
        ['Offline hangok (PRO)', 'Tölts le neurális hangokat offline kiejtéshez.'],
        ['オフライン音声 (PRO)', 'ニューラル音声をダウンロードしてオフラインで聞けます。'],
        ['离线语音 (PRO)', '下载神经语音，离线也能听发音。'],
        ['Офлайн-голоса (PRO)', 'Скачайте нейроголоса для прослушивания без сети.'],
      ),
    ],
  },

  settings_storage: {
    description: d('Sincronização com o Google Drive.', 'Google Drive sync.', 'Sincronización con Google Drive.', 'Synchronisation Google Drive.', 'Sincronizzazione Google Drive.', 'Google-Drive-Synchronisierung.', 'Google Drive szinkron.', 'Google ドライブ同期。', 'Google 云端硬盘同步。', 'Синхронизация с Google Диском.'),
    steps: [
      s(SECTION,
        ['Sincronização', 'Conecte seu Google Drive para guardar seus livros na sua própria conta e acessá-los em qualquer aparelho.'],
        ['Sync', 'Connect Google Drive to keep your books in your own account and open them on any device.'],
        ['Sincronización', 'Conecta Google Drive para guardar tus libros en tu cuenta y abrirlos en cualquier dispositivo.'],
        ['Synchronisation', 'Connectez Google Drive pour garder vos livres sur votre compte et les ouvrir partout.'],
        ['Sincronizzazione', 'Collega Google Drive per tenere i libri sul tuo account e aprirli ovunque.'],
        ['Synchronisierung', 'Verbinde Google Drive, um deine Bücher im eigenen Konto zu speichern.'],
        ['Szinkron', 'Csatlakoztasd a Google Drive-ot, hogy a könyveid bármely eszközön elérhetők legyenek.'],
        ['同期', 'Google ドライブを接続すると、本を自分のアカウントに保存しどの端末でも開けます。'],
        ['同步', '连接 Google 云端硬盘，把书保存在自己的账户里，任何设备都能打开。'],
        ['Синхронизация', 'Подключите Google Диск, чтобы хранить книги в своём аккаунте.'],
      ),
    ],
  },

  settings_sounds: {
    description: d('Avisos, sons e volume.', 'Alerts, sounds and volume.', 'Avisos, sonidos y volumen.', 'Alertes, sons et volume.', 'Avvisi, suoni e volume.', 'Hinweise, Töne und Lautstärke.', 'Értesítések, hangok, hangerő.', '通知、サウンド、音量。', '提醒、声音和音量。', 'Уведомления, звуки и громкость.'),
    steps: [
      s(SECTION,
        ['Notificação & som', 'Ative ou desative avisos e ajuste os sons e o volume do app.'],
        ['Notifications & sound', 'Turn alerts on or off and adjust app sounds and volume.'],
        ['Notificaciones y sonido', 'Activa o desactiva avisos y ajusta sonidos y volumen.'],
        ['Notifications et son', 'Activez ou coupez les alertes et réglez sons et volume.'],
        ['Notifiche e suoni', 'Attiva o disattiva avvisi e regola suoni e volume.'],
        ['Benachrichtigungen & Ton', 'Schalte Hinweise ein/aus und passe Töne und Lautstärke an.'],
        ['Értesítés és hang', 'Kapcsold ki/be az értesítéseket, állítsd a hangokat.'],
        ['通知とサウンド', '通知のオン/オフ、サウンドと音量を調整します。'],
        ['通知与声音', '开关提醒，调整应用声音和音量。'],
        ['Уведомления и звук', 'Включайте уведомления и настраивайте звук и громкость.'],
      ),
    ],
  },

  settings_update: {
    description: d('Versão do app.', 'App version.', 'Versión de la app.', 'Version de l’app.', 'Versione dell’app.', 'App-Version.', 'Alkalmazás verzió.', 'アプリのバージョン。', '应用版本。', 'Версия приложения.'),
    steps: [
      s(SECTION,
        ['Atualizações', 'Veja a versão instalada e force a atualização se algo parecer desatualizado.'],
        ['Updates', 'See the installed version and force an update if something looks outdated.'],
        ['Actualizaciones', 'Mira la versión instalada y fuerza la actualización si algo parece antiguo.'],
        ['Mises à jour', 'Voyez la version installée et forcez la mise à jour si besoin.'],
        ['Aggiornamenti', 'Vedi la versione installata e forza l’aggiornamento se serve.'],
        ['Updates', 'Sieh die installierte Version und erzwinge ein Update, falls nötig.'],
        ['Frissítések', 'Nézd meg a verziót, és frissíts, ha valami elavultnak tűnik.'],
        ['アップデート', 'インストール済みのバージョンを確認し、必要なら強制更新します。'],
        ['更新', '查看已安装版本，必要时强制更新。'],
        ['Обновления', 'Посмотрите версию и обновите принудительно при необходимости.'],
      ),
    ],
  },

  settings_upgrade: {
    description: d('Seu plano.', 'Your plan.', 'Tu plan.', 'Votre forfait.', 'Il tuo piano.', 'Dein Tarif.', 'Előfizetésed.', 'プラン。', '你的套餐。', 'Ваш тариф.'),
    steps: [
      s(SECTION,
        ['Plano PRO', 'Veja seu plano e libere recursos PRO, como tradução e vozes offline e conversas ilimitadas.'],
        ['PRO plan', 'See your plan and unlock PRO features like offline translation, offline voices and unlimited chats.'],
        ['Plan PRO', 'Mira tu plan y desbloquea funciones PRO como traducción y voces sin conexión.'],
        ['Forfait PRO', 'Voyez votre forfait et débloquez les fonctions PRO : traduction et voix hors ligne.'],
        ['Piano PRO', 'Vedi il tuo piano e sblocca le funzioni PRO: traduzione e voci offline.'],
        ['PRO-Tarif', 'Sieh deinen Tarif und schalte PRO frei: Offline-Übersetzung und -Stimmen.'],
        ['PRO csomag', 'Nézd meg a csomagod, és nyisd meg a PRO funkciókat: offline fordítás és hangok.'],
        ['PRO プラン', 'プランを確認し、オフライン翻訳や音声などの PRO 機能を利用できます。'],
        ['PRO 套餐', '查看套餐，解锁离线翻译、离线语音等 PRO 功能。'],
        ['Тариф PRO', 'Посмотрите тариф и откройте PRO: офлайн-перевод и голоса.'],
      ),
    ],
  },
};
